import { can } from "modules/settings/accessRoles";
import React, { useCallback, useMemo, useRef } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Divider,
    Stack,
    TextField,
    Typography,
    Table,
    TableHead,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import Slide from "@mui/material/Slide";
import {
    DataGridPro,
    GridToolbarContainer,
    useGridApiRef,
} from "@mui/x-data-grid-pro";
import { LicenseInfo } from "@mui/x-license";

import { downloadCSV } from "../../../utils/csvUtils";
import RiderRatesTestDialog from "../../../components/RiderRatesTestDialog";
import { buildRiders, computeBaseSummary, getEffectiveBaseCharge, getTestableBaseChargeItems, isRiderEffective } from "../../../setup/helpers";
import { setSnackbar } from "../../snackbar/api/snackbar";
import {
    updateLocalVendorRiderRates,
    getVendorRiderRatesByVendorUuid,
    createVendorRiderRate,
    updateVendorRiderRate,
    deleteVendorRiderRate,
} from "../api/actions";

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUX);

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const RIDER_TYPES = ["Distribution", "Generation", "Transmission"];
const RIDER_TYPE_ORDER = { Distribution: 0, Transmission: 1, Generation: 2 };
const RATE_BASES = ["kWh", "Flat", "Base"];
const RATE_BASIS_LABELS = { kWh: "kWh", Flat: "Flat", Base: "Base (Dist)" };
const RATE_TYPES = ["$", "%"];

const CSV_COLUMNS = [
    "riderName",
    "riderType",
    "minKwh",
    "maxKwh",
    "rateBasis",
    "rateSummer",
    "rateWinter",
    "rateType",
    "startDate",
    "endDate",
];

const GRID_HEIGHT = 640;

const EMPTY_ROW_SELECTION = { type: "include", ids: new Set() };

function getSelectedRowCount(selectionModel, totalRows) {
    if (!selectionModel || !selectionModel.ids) {
        return 0;
    }
    if (selectionModel.type === "include") {
        return selectionModel.ids.size;
    }
    return Math.max(0, totalRows - selectionModel.ids.size);
}

function getSelectedRowIds(selectionModel, allRowIds) {
    if (!selectionModel || !selectionModel.ids) {
        return new Set();
    }
    if (selectionModel.type === "include") {
        return selectionModel.ids;
    }
    return new Set(allRowIds.filter((id) => !selectionModel.ids.has(id)));
}

function parseCsvLine(line) {
    const out = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    cur += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                cur += ch;
            }
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === ",") {
            out.push(cur);
            cur = "";
        } else {
            cur += ch;
        }
    }
    out.push(cur);
    return out.map((s) => s.trim());
}

function parseCsv(text) {
    const lines = text
        .replace(/^\uFEFF/, "")
        .split(/\r?\n/)
        .filter((l) => l.trim() !== "");
    if (lines.length === 0) {
        return { headers: [], records: [] };
    }
    const rawHeaders = parseCsvLine(lines[0]);
    const headerMap = rawHeaders.map((h) => {
        const match = CSV_COLUMNS.find(
            (c) => c.toLowerCase() === h.toLowerCase().replace(/\s+/g, "")
        );
        return match || null;
    });
    const records = lines.slice(1).map((line) => {
        const cells = parseCsvLine(line);
        const record = {};
        headerMap.forEach((field, idx) => {
            if (field) {
                record[field] = cells[idx] !== undefined ? cells[idx] : "";
            }
        });
        return record;
    });
    return { headers: rawHeaders, records };
}

function isBlank(value) {
    return value === undefined || value === null || String(value).trim() === "";
}

function isNumeric(value) {
    if (isBlank(value)) return false;
    return !Number.isNaN(Number(value));
}

function validateRecord(record) {
    const errors = [];
    if (isBlank(record.riderName)) {
        errors.push("Name is required");
    }
    if (!RIDER_TYPES.includes(record.riderType)) {
        errors.push(`Type must be one of ${RIDER_TYPES.join(", ")}`);
    }
    if (!RATE_BASES.includes(record.rateBasis)) {
        errors.push(`Rate Basis must be one of ${RATE_BASES.join(", ")}`);
    }
    if (!RATE_TYPES.includes(record.rateType)) {
        errors.push(`Rate Type must be one of ${RATE_TYPES.join(", ")}`);
    }
    ["minKwh", "maxKwh", "rateSummer", "rateWinter"].forEach((field) => {
        if (!isNumeric(record[field])) {
            errors.push(`${field} must be numeric`);
        }
    });
    if (isBlank(record.startDate) || !moment(record.startDate).isValid()) {
        errors.push("Start Date is required and must be a valid date");
    }
    if (!isBlank(record.endDate)) {
        if (!moment(record.endDate).isValid()) {
            errors.push("End Date must be a valid date");
        } else if (
            moment(record.endDate).isValid() &&
            moment(record.startDate).isValid() &&
            moment(record.endDate).isBefore(moment(record.startDate))
        ) {
            errors.push("End Date cannot be before Start Date");
        }
    }
    return errors;
}

function recordToLineItem(record) {
    return {
        riderName: record.riderName,
        riderType: record.riderType,
        minKwh: Number(record.minKwh),
        maxKwh: Number(record.maxKwh),
        rateBasis: record.rateBasis,
        rateSummer: Number(record.rateSummer),
        rateWinter: Number(record.rateWinter),
        rateType: record.rateType,
        startDate: record.startDate,
        endDate: record.endDate || "",
        _gridId: `import-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    };
}

function createBlankLineItem() {
    return {
        riderName: "",
        riderType: "",
        minKwh: 0,
        maxKwh: 0,
        rateBasis: "",
        rateSummer: 0,
        rateWinter: 0,
        rateType: "",
        startDate: "",
        endDate: "",
        _gridId: `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    };
}

function sortRiderItems(items) {
    return [...(items || [])].sort((a, b) => {
        const orderA = RIDER_TYPE_ORDER[a.riderType] ?? 99;
        const orderB = RIDER_TYPE_ORDER[b.riderType] ?? 99;
        if (orderA !== orderB) {
            return orderA - orderB;
        }
        return String(a.riderName || "").localeCompare(String(b.riderName || ""));
    });
}

function gridIdForItem(item, index) {
    return item.uuid || item._gridId || `row-${index}`;
}

function itemToGridRow(item, index) {
    return {
        ...item,
        // DataGrid requires `id` as the row key. Preserve the DB numeric id
        // separately so edits don't look like brand-new rows on save.
        _dbId: item.id,
        id: gridIdForItem(item, index),
        rowNum: index + 1,
    };
}

function gridRowToItem(row) {
    const { id, rowNum, _dbId, ...rest } = row;
    const item = { ...rest };
    if (_dbId !== undefined && _dbId !== null) {
        item.id = _dbId;
    }
    return item;
}

function isNewRiderItem(item) {
    // New rows are local-only until save; persisted rows always have a uuid.
    return !item.uuid;
}

function stripLocalGridFields(item) {
    const { _gridId, _dbId, ...rest } = item;
    return rest;
}

function getTestableRiderItems(items, toDelete = []) {
    const deleteSet = new Set(toDelete || []);
    return (items || [])
        .filter((item) => !(item.uuid && deleteSet.has(item.uuid)))
        .map(({ _gridId, ...rider }) => rider);
}

function RateCell({ value }) {
    const num = Number(value);
    const display = value === "" || value === null || value === undefined ? "" : num;
    const isNegative = !Number.isNaN(num) && num < 0;
    return (
        <span style={{ color: isNegative ? "#d32f2f" : "inherit", fontVariantNumeric: "tabular-nums" }}>
            {display}
        </span>
    );
}

RateCell.propTypes = {
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function VendorRiderRatesToolbar({
    rowCount,
    selectedCount,
    canEdit,
    isDirty,
    onNewRow,
    onDeleteRows,
    onSave,
    onUndo,
    onExport,
    onImport,
    onTest,
}) {
    return (
        <GridToolbarContainer sx={{ px: 1, py: 1, gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ flexGrow: 1, alignSelf: "center" }}>
                {rowCount} row{rowCount === 1 ? "" : "s"} · {selectedCount} selected
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {canEdit && (
                    <>
                        <Button
                            size="small"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={onDeleteRows}
                            disabled={selectedCount === 0}
                            variant="outlined"
                            color="error"
                        >
                            Delete rows
                        </Button>
                        <Button size="small" startIcon={<AddIcon />} onClick={onNewRow} variant="outlined">
                            New row
                        </Button>
                        <Button
                            size="small"
                            startIcon={<UndoIcon />}
                            onClick={onUndo}
                            disabled={!isDirty}
                            variant="outlined"
                        >
                            Undo
                        </Button>
                        <Button
                            size="small"
                            startIcon={<SaveIcon />}
                            onClick={onSave}
                            disabled={!isDirty}
                            variant="contained"
                            color="secondary"
                        >
                            Save
                        </Button>
                    </>
                )}
                <Button size="small" startIcon={<FileDownloadIcon />} onClick={onExport} variant="outlined">
                    Export
                </Button>
                <Button
                    size="small"
                    startIcon={<AssignmentTurnedInIcon />}
                    onClick={onTest}
                    variant="outlined"
                >
                    Test outputs
                </Button>
                {canEdit && (
                    <Button size="small" startIcon={<UploadFileIcon />} onClick={onImport} variant="outlined">
                        Import
                    </Button>
                )}
            </Stack>
        </GridToolbarContainer>
    );
}

VendorRiderRatesToolbar.propTypes = {
    rowCount: PropTypes.number.isRequired,
    selectedCount: PropTypes.number.isRequired,
    canEdit: PropTypes.bool.isRequired,
    isDirty: PropTypes.bool.isRequired,
    onNewRow: PropTypes.func.isRequired,
    onDeleteRows: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onUndo: PropTypes.func.isRequired,
    onExport: PropTypes.func.isRequired,
    onImport: PropTypes.func.isRequired,
    onTest: PropTypes.func.isRequired,
};

function ActiveCellHint({ activeCell, canEdit }) {
    if (!canEdit) {
        return (
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                View only — you do not have permission to edit rider rates.
            </Typography>
        );
    }

    if (!activeCell) {
        return (
            <Box
                sx={{
                    mb: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    border: "1px dashed",
                    borderColor: "divider",
                }}
            >
                <Typography variant="caption" color="textSecondary">
                    Click a cell to select it — the active cell will be highlighted with a blue border.
                    Then type to edit, or use Enter / F2, Tab / arrow keys, Shift + arrows for ranges,
                    and Cmd+C / Cmd+V to copy and paste.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                mb: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                bgcolor: "rgba(25, 118, 210, 0.08)",
                border: "1px solid",
                borderColor: "primary.main",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
            }}
        >
            <Typography
                variant="caption"
                component="span"
                sx={{
                    fontWeight: 600,
                    color: "primary.main",
                    bgcolor: "background.paper",
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "primary.light",
                }}
            >
                Active: Row {activeCell.rowNum} · {activeCell.column}
            </Typography>
            <Typography variant="caption" color="textSecondary">
                Type to edit · Enter / F2 · Tab / arrows move · Shift + arrows range · Cmd+C / Cmd+V
            </Typography>
        </Box>
    );
}

ActiveCellHint.propTypes = {
    activeCell: PropTypes.shape({
        rowNum: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        column: PropTypes.string.isRequired,
    }),
    canEdit: PropTypes.bool.isRequired,
};

function VendorRiderRatesTable(props) {
    const { vendor } = props;
    const effectiveAsOfDate = props.effectiveAsOfDate || moment().format("YYYY-MM-DD");
    const vendorUuid = vendor?.uuid;
    const dispatch = useDispatch();
    const apiRef = useGridApiRef();
    const nextLocalId = useRef(1);
    const savedSnapshotRef = useRef([]);

    const [importOpen, setImportOpen] = React.useState(false);
    const [importText, setImportText] = React.useState("");
    const [parsedRecords, setParsedRecords] = React.useState([]);
    const [hasParsed, setHasParsed] = React.useState(false);
    const [rowSelectionModel, setRowSelectionModel] = React.useState(EMPTY_ROW_SELECTION);
    const [activeCell, setActiveCell] = React.useState(null);
    const [testDialogOpen, setTestDialogOpen] = React.useState(false);
    const [testKwhInput, setTestKwhInput] = React.useState(1);
    const [testBillingDate, setTestBillingDate] = React.useState(moment().format("YYYY-MM-DD"));
    const [riderRateTestProductSummary, setRiderRateTestProductSummary] = React.useState(null);
    const [riderRateTestProduct, setRiderRateTestProduct] = React.useState([]);

    const canAddOrUpdate = can(props.user.details, "billingRates");

    const riderRates = props.vendors.data.vendorRiderRates || {
        items: [],
        toUpdate: [],
        toDelete: [],
    };

    const baseCharges = props.vendors.data.vendorBaseCharges || {
        items: [],
        toUpdate: [],
        toDelete: [],
    };

    const sortedItems = useMemo(
        () => sortRiderItems(riderRates.items.filter((item) => item.riderType !== "Base")),
        [riderRates.items]
    );

    const testableItems = useMemo(
        () =>
            getTestableRiderItems(riderRates.items, riderRates.toDelete).filter(
                (item) => item.riderType !== "Base"
            ),
        [riderRates.items, riderRates.toDelete]
    );

    const testableBaseCharges = useMemo(
        () => getTestableBaseChargeItems(baseCharges.items, baseCharges.toDelete),
        [baseCharges.items, baseCharges.toDelete]
    );

    const rows = useMemo(
        () => sortedItems.map(itemToGridRow),
        [sortedItems]
    );

    const updateActiveCellFromParams = useCallback(
        (params) => {
            const rowIndex = rows.findIndex((row) => row.id === params.id);
            setActiveCell({
                rowNum: params.row?.rowNum ?? (rowIndex >= 0 ? rowIndex + 1 : params.id),
                column: params.colDef?.headerName || params.field,
            });
        },
        [rows]
    );

    React.useEffect(() => {
        const api = apiRef.current;
        if (!api) {
            return undefined;
        }

        return api.subscribeEvent("cellFocusIn", (params) => {
            updateActiveCellFromParams(params);
        });
    }, [apiRef, updateActiveCellFromParams]);

    const handleCellClick = useCallback(
        (params) => {
            updateActiveCellFromParams(params);
        },
        [updateActiveCellFromParams]
    );

    const columns = useMemo(
        () => [
            {
                field: "rowNum",
                headerName: "#",
                width: 52,
                editable: false,
                sortable: false,
                filterable: false,
            },
            {
                field: "riderName",
                headerName: "Name",
                flex: 1.4,
                minWidth: 180,
                editable: canAddOrUpdate,
            },
            {
                field: "riderType",
                headerName: "Type",
                flex: 1,
                minWidth: 130,
                type: "singleSelect",
                valueOptions: RIDER_TYPES,
                editable: canAddOrUpdate,
            },
            {
                field: "minKwh",
                headerName: "Min KWH",
                type: "number",
                width: 100,
                editable: canAddOrUpdate,
            },
            {
                field: "maxKwh",
                headerName: "Max KWH",
                type: "number",
                width: 110,
                editable: canAddOrUpdate,
            },
            {
                field: "rateBasis",
                headerName: "Rate Basis",
                width: 120,
                type: "singleSelect",
                valueOptions: RATE_BASES.map((v) => ({
                    value: v,
                    label: RATE_BASIS_LABELS[v] || v,
                })),
                editable: canAddOrUpdate,
                valueFormatter: (value) => RATE_BASIS_LABELS[value] || value,
            },
            {
                field: "rateSummer",
                headerName: "Summer Rate",
                type: "number",
                flex: 1,
                minWidth: 120,
                editable: canAddOrUpdate,
                renderCell: (params) => <RateCell value={params.value} />,
            },
            {
                field: "rateWinter",
                headerName: "Winter Rate",
                type: "number",
                flex: 1,
                minWidth: 120,
                editable: canAddOrUpdate,
                renderCell: (params) => <RateCell value={params.value} />,
            },
            {
                field: "rateType",
                headerName: "Rate Type",
                width: 100,
                type: "singleSelect",
                valueOptions: RATE_TYPES,
                editable: canAddOrUpdate,
            },
            {
                field: "startDate",
                headerName: "Start",
                width: 120,
                editable: canAddOrUpdate,
                valueFormatter: (value) => {
                    if (!value) return "";
                    return moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : value;
                },
            },
            {
                field: "endDate",
                headerName: "End",
                width: 120,
                editable: canAddOrUpdate,
                valueFormatter: (value) => {
                    if (!value) return "";
                    return moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : value;
                },
            },
        ],
        [canAddOrUpdate]
    );

    const syncLocalState = useCallback(
        (items, toUpdate, toDelete) => {
            props.updateLocalVendorRiderRates({
                items,
                toUpdate: toUpdate || [],
                toDelete: toDelete || [],
            });
        },
        [props]
    );

    const saveAllLineItems = useCallback(
        (rr) => {
            const toDelete = rr.toDelete || [];
            props.updateLocalVendorRiderRates(rr);
            (rr.toUpdate || []).forEach((targetUuid) => {
                if (toDelete.includes(targetUuid)) {
                    return;
                }
                const idx = rr.items.findIndex((x) => x.uuid === targetUuid);
                if (idx !== -1) {
                    props.updateVendorRiderRate(stripLocalGridFields(rr.items[idx]));
                }
            });
            toDelete.forEach((targetUuid) => {
                props.deleteVendorRiderRate(targetUuid, vendorUuid);
            });
            const ridersToCreate = [];
            const existingRiders = [];
            for (let x = 0; x < rr.items.length; x++) {
                const item = rr.items[x];
                // Drop rows marked for deletion so they don't reappear after save
                if (item.uuid && toDelete.includes(item.uuid)) {
                    continue;
                }
                if (isNewRiderItem(item)) {
                    ridersToCreate.push(stripLocalGridFields(item));
                } else {
                    existingRiders.push(item);
                }
            }
            if (ridersToCreate.length > 0) {
                props.createVendorRiderRate(ridersToCreate, vendorUuid);
            }
            props.updateLocalVendorRiderRates({
                items: existingRiders,
                toUpdate: [],
                toDelete: [],
            });
            dispatch(setSnackbar(true, "success", "Vendor rider rates saved."));
        },
        [dispatch, props, vendorUuid]
    );

    const handleProcessRowUpdate = useCallback(
        (newRow) => {
            const updatedItem = gridRowToItem(newRow);
            const items = (riderRates.items || []).map((item, index) => {
                if (gridIdForItem(item, index) === newRow.id) {
                    return updatedItem;
                }
                return item;
            });
            const toUpdate = [...(riderRates.toUpdate || [])];
            if (updatedItem.uuid && !toUpdate.includes(updatedItem.uuid)) {
                toUpdate.push(updatedItem.uuid);
            }
            syncLocalState(items, toUpdate, riderRates.toDelete);
            return newRow;
        },
        [riderRates.items, riderRates.toUpdate, riderRates.toDelete, syncLocalState]
    );

    const handleProcessRowUpdateError = useCallback(
        (error) => {
            dispatch(setSnackbar(true, "error", error?.message || "Failed to update row."));
        },
        [dispatch]
    );

    const handleNewRow = useCallback(() => {
        const newItem = createBlankLineItem();
        syncLocalState(
            [...(riderRates.items || []), newItem],
            riderRates.toUpdate || [],
            riderRates.toDelete || []
        );
        setTimeout(() => {
            const rowCount = apiRef.current?.getRowsCount?.() ?? rows.length;
            if (apiRef.current) {
                apiRef.current.scrollToIndexes({ rowIndex: rowCount });
                apiRef.current.setCellFocus(newItem._gridId, "riderName");
                updateActiveCellFromParams({
                    id: newItem._gridId,
                    field: "riderName",
                    colDef: { headerName: "Name" },
                    row: { rowNum: rowCount + 1 },
                });
            }
        }, 50);
    }, [apiRef, riderRates, rows.length, syncLocalState, updateActiveCellFromParams]);

    const handleDeleteRows = useCallback(() => {
        const items = riderRates.items || [];
        const allRowIds = items.map((item, index) => gridIdForItem(item, index));
        const selected = getSelectedRowIds(rowSelectionModel, allRowIds);

        // Brand-new unsaved rows (no uuid) are removed right away - nothing to persist.
        const remaining = items.filter((item, index) => {
            const isSelected = selected.has(gridIdForItem(item, index));
            return !(isSelected && !item.uuid);
        });

        // Existing (persisted) rows are toggled into a pending-delete list but kept
        // visible until Save. Selecting an already-pending row again restores it.
        const pendingDelete = new Set(riderRates.toDelete || []);
        items.forEach((item, index) => {
            if (item.uuid && selected.has(gridIdForItem(item, index))) {
                if (pendingDelete.has(item.uuid)) {
                    pendingDelete.delete(item.uuid);
                } else {
                    pendingDelete.add(item.uuid);
                }
            }
        });

        const toDelete = Array.from(pendingDelete);
        const toUpdate = (riderRates.toUpdate || []).filter((u) => !toDelete.includes(u));
        syncLocalState(remaining, toUpdate, toDelete);
        setRowSelectionModel({ type: "include", ids: new Set() });
    }, [riderRates, rowSelectionModel, syncLocalState]);

    const handleSave = useCallback(() => {
        saveAllLineItems(props.vendors.data.vendorRiderRates);
    }, [props.vendors.data.vendorRiderRates, saveAllLineItems]);

    const handleExport = useCallback(() => {
        const exportRows = (riderRates.items || []).map((item) => {
            const row = {};
            CSV_COLUMNS.forEach((col) => {
                row[col] = item[col] ?? "";
            });
            return row;
        });
        downloadCSV(
            exportRows,
            `vendor-rider-rates-${moment().format("YYYY-MM-DD")}.csv`
        );
    }, [riderRates.items]);

    const handleDownloadTemplate = () => {
        const sample = {
            riderName: "Example Rider",
            riderType: "Distribution",
            minKwh: 0,
            maxKwh: 800,
            rateBasis: "kWh",
            rateSummer: 0.0451,
            rateWinter: 0.0389,
            rateType: "$",
            startDate: moment().format("YYYY-MM-DD"),
            endDate: "",
        };
        downloadCSV([sample], "vendor-rider-rates-template.csv");
    };

    const handleFileUpload = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            setImportText(e.target.result || "");
            runParse(e.target.result || "");
        };
        reader.readAsText(file);
        event.target.value = "";
    };

    const runParse = (text) => {
        const { records } = parseCsv(text);
        const validated = records.map((record) => ({
            record,
            errors: validateRecord(record),
        }));
        setParsedRecords(validated);
        setHasParsed(true);
    };

    const importErrorCount = parsedRecords.reduce(
        (acc, r) => acc + (r.errors.length > 0 ? 1 : 0),
        0
    );
    const canLoadImport =
        hasParsed && parsedRecords.length > 0 && importErrorCount === 0;

    const handleLoadImport = () => {
        const newItems = parsedRecords.map((r, idx) => ({
            ...recordToLineItem(r.record),
            _gridId: `import-${Date.now()}-${idx}-${nextLocalId.current++}`,
        }));
        syncLocalState(
            [...(riderRates.items || []), ...newItems],
            riderRates.toUpdate || [],
            riderRates.toDelete || []
        );
        dispatch(
            setSnackbar(
                true,
                "success",
                `Loaded ${newItems.length} rider rate(s). Review and Save to apply.`
            )
        );
        handleImportClose();
    };

    const handleImportOpen = () => setImportOpen(true);
    const handleImportClose = () => {
        setImportOpen(false);
        setImportText("");
        setParsedRecords([]);
        setHasParsed(false);
    };

    const selectedCount = getSelectedRowCount(rowSelectionModel, rows.length);

    const newCount = (riderRates.items || []).filter((item) => !item.uuid).length;
    const editedCount = (riderRates.toUpdate || []).length;
    const deleteCount = (riderRates.toDelete || []).length;
    const dirtyCount = newCount + editedCount + deleteCount;
    const isDirty = dirtyCount > 0;

    const calculateTestRiderRate = React.useCallback(() => {
        const currentTestKwhValue = Number(testKwhInput) || 0;
        const ridersForTest = getTestableRiderItems(
            riderRates.items,
            riderRates.toDelete
        ).filter((item) => item.riderType !== "Base");
        const baseForTest = getEffectiveBaseCharge(
            getTestableBaseChargeItems(baseCharges.items, baseCharges.toDelete),
            testBillingDate
        );
        const testRiderResult = buildRiders(
            currentTestKwhValue,
            {},
            ridersForTest,
            0,
            testBillingDate,
            1,
            currentTestKwhValue,
            baseForTest,
            true
        );
        setRiderRateTestProductSummary(testRiderResult.bill);
        setRiderRateTestProduct(testRiderResult.riders);
    }, [
        testKwhInput,
        testBillingDate,
        riderRates.items,
        riderRates.toDelete,
        baseCharges.items,
        baseCharges.toDelete,
    ]);

    const handleTestOpen = useCallback(() => {
        const billingDate = moment().format("YYYY-MM-DD");
        const kwh = 1;
        setTestKwhInput(kwh);
        setTestBillingDate(billingDate);
        const ridersForTest = getTestableRiderItems(
            riderRates.items,
            riderRates.toDelete
        ).filter((item) => item.riderType !== "Base");
        const baseForTest = getEffectiveBaseCharge(
            getTestableBaseChargeItems(baseCharges.items, baseCharges.toDelete),
            billingDate
        );
        const testRiderResult = buildRiders(kwh, {}, ridersForTest, 0, billingDate, 1, kwh, baseForTest, true);
        setRiderRateTestProductSummary(testRiderResult.bill);
        setRiderRateTestProduct(testRiderResult.riders);
        setTestDialogOpen(true);
    }, [riderRates.items, riderRates.toDelete, baseCharges.items, baseCharges.toDelete]);

    const handleTestClose = useCallback(() => {
        setTestDialogOpen(false);
        setRiderRateTestProductSummary(null);
        setRiderRateTestProduct([]);
    }, []);

    const handleTestKwhChange = useCallback((event) => {
        setTestKwhInput(Number(event.target.value));
    }, []);

    const handleTestBillingDateChange = useCallback((event) => {
        setTestBillingDate(event.target.value);
    }, []);

    React.useEffect(() => {
        if (!testDialogOpen) {
            return;
        }
        calculateTestRiderRate();
    }, [
        testDialogOpen,
        testKwhInput,
        testBillingDate,
        calculateTestRiderRate,
        riderRates.items,
        riderRates.toDelete,
        baseCharges.items,
        baseCharges.toDelete,
    ]);

    const testDialogLoading = false;

    const testLoadingMessage = "Calculating rider rate outputs…";

    const testBannerMessage = isDirty
        ? "Testing unsaved grid changes. Results reflect the current grid, not the last saved vendor rider rates."
        : "";

    const testBaseSummary = useMemo(
        () => computeBaseSummary(testableBaseCharges, testBillingDate, Number(testKwhInput) || 0),
        [testableBaseCharges, testBillingDate, testKwhInput]
    );

    // Whenever the grid is in a clean (saved) state, capture it as the baseline
    // that Undo will revert to.
    React.useEffect(() => {
        if (!isDirty) {
            savedSnapshotRef.current = (riderRates.items || []).map((item) => ({ ...item }));
        }
    }, [isDirty, riderRates.items]);

    const handleUndo = useCallback(() => {
        const baseline = (savedSnapshotRef.current || []).map((item) => ({ ...item }));
        syncLocalState(baseline, [], []);
        setRowSelectionModel({ type: "include", ids: new Set() });
        dispatch(setSnackbar(true, "info", "Reverted to last saved state."));
    }, [dispatch, syncLocalState]);

    const toDeleteSet = useMemo(
        () => new Set(riderRates.toDelete || []),
        [riderRates.toDelete]
    );
    const toUpdateSet = useMemo(
        () => new Set(riderRates.toUpdate || []),
        [riderRates.toUpdate]
    );

    const getRowClassName = useCallback(
        (params) => {
            const classes = [
                params.indexRelativeToCurrentPage % 2 === 0
                    ? "vendor-rider-rate-row-even"
                    : "",
            ];
            if (isRiderEffective(params.row, effectiveAsOfDate)) {
                classes.push("vendor-rider-rate-row-effective");
            }
            const rowUuid = params.row.uuid;
            if (rowUuid && toDeleteSet.has(rowUuid)) {
                classes.push("vendor-rider-rate-row-deleted");
            } else if (!rowUuid) {
                classes.push("vendor-rider-rate-row-new");
            } else if (toUpdateSet.has(rowUuid)) {
                classes.push("vendor-rider-rate-row-edited");
            }
            return classes.filter(Boolean).join(" ");
        },
        [toDeleteSet, toUpdateSet, effectiveAsOfDate]
    );

    const toolbarSlot = useCallback(
        () => (
            <VendorRiderRatesToolbar
                rowCount={rows.length}
                selectedCount={selectedCount}
                canEdit={canAddOrUpdate}
                isDirty={isDirty}
                dirtyCount={dirtyCount}
                onNewRow={handleNewRow}
                onDeleteRows={handleDeleteRows}
                onSave={handleSave}
                onUndo={handleUndo}
                onExport={handleExport}
                onImport={handleImportOpen}
                onTest={handleTestOpen}
            />
        ),
        [
            rows.length,
            selectedCount,
            canAddOrUpdate,
            isDirty,
            dirtyCount,
            handleNewRow,
            handleDeleteRows,
            handleSave,
            handleUndo,
            handleExport,
            handleTestOpen,
        ]
    );

    return (
        <Card elevation={3}>
            <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Vendor Rider Rates
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Distribution, Generation, and Transmission riders for this vendor.
                </Typography>

                <Box
                    sx={{
                        height: GRID_HEIGHT,
                        width: "100%",
                        "@keyframes vrrSavePulse": {
                            "0%": { boxShadow: "0 0 0 0 rgba(237,108,2,0.55)" },
                            "70%": { boxShadow: "0 0 0 6px rgba(237,108,2,0)" },
                            "100%": { boxShadow: "0 0 0 0 rgba(237,108,2,0)" },
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: 600,
                        },
                        "& .MuiDataGrid-columnHeader:focus-within": {
                            outline: "none",
                        },
                        "& .MuiDataGrid-columnHeaders .MuiDataGrid-iconSeparator": {
                            color: "divider",
                        },
                        "& .MuiDataGrid-columnHeader .MuiCheckbox-root": {
                            color: "action.active",
                        },
                        "& .MuiDataGrid-columnHeader .MuiCheckbox-root.Mui-checked": {
                            color: "primary.main",
                        },
                        "& .vendor-rider-rate-row-even": {
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                        },
                        "& .vendor-rider-rate-row-effective:not(.vendor-rider-rate-row-deleted)": {
                            boxShadow: "inset 3px 0 0 #1976d2",
                        },
                        "& .vendor-rider-rate-row-new, & .vendor-rider-rate-row-new.vendor-rider-rate-row-even": {
                            backgroundColor: "rgba(46, 125, 50, 0.1)",
                        },
                        "& .vendor-rider-rate-row-edited, & .vendor-rider-rate-row-edited.vendor-rider-rate-row-even": {
                            backgroundColor: "rgba(237, 108, 2, 0.1)",
                        },
                        "& .vendor-rider-rate-row-deleted, & .vendor-rider-rate-row-deleted.vendor-rider-rate-row-even": {
                            backgroundColor: "rgba(211, 47, 47, 0.1)",
                        },
                        "& .vendor-rider-rate-row-deleted .MuiDataGrid-cell": {
                            textDecoration: "line-through",
                            color: "rgba(0, 0, 0, 0.45)",
                        },
                        "& .MuiDataGrid-cell": {
                            cursor: canAddOrUpdate ? "cell" : "default",
                        },
                        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                            outline: "2px solid #1976d2",
                            outlineOffset: -2,
                            backgroundColor: "rgba(25, 118, 210, 0.12)",
                            boxShadow: "inset 0 0 0 1px #1976d2",
                            zIndex: 1,
                        },
                        "& .MuiDataGrid-cell.Mui-selected": {
                            backgroundColor: "rgba(25, 118, 210, 0.2)",
                            borderColor: "#1976d2",
                        },
                        "& .MuiDataGrid-cell.Mui-selected:focus, & .MuiDataGrid-cell.Mui-selected:focus-within": {
                            backgroundColor: "rgba(25, 118, 210, 0.28)",
                        },
                    }}
                >
                    <DataGridPro
                        apiRef={apiRef}
                        rows={rows}
                        columns={columns}
                        loading={props.vendors.riderRatesAreLoading}
                        editMode="cell"
                        cellSelection={canAddOrUpdate}
                        checkboxSelection={canAddOrUpdate}
                        disableRowSelectionOnClick
                        rowSelectionModel={rowSelectionModel}
                        onRowSelectionModelChange={setRowSelectionModel}
                        onCellClick={handleCellClick}
                        processRowUpdate={handleProcessRowUpdate}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        rowHeight={36}
                        columnHeaderHeight={40}
                        getRowClassName={getRowClassName}
                        slots={{ toolbar: toolbarSlot }}
                        showToolbar
                        hideFooter
                    />
                </Box>
            </CardContent>

            <Dialog
                open={importOpen}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="lg"
                onClose={handleImportClose}
                aria-labelledby="vendor-rider-rate-import-title"
            >
                <DialogTitle id="vendor-rider-rate-import-title">
                    Import Vendor Rider Rates
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <DialogContentText>
                            Upload or paste a CSV of rider rates. Valid rows are appended to the
                            grid for review; nothing is saved until you press Save.
                        </DialogContentText>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button variant="outlined" color="secondary" onClick={handleDownloadTemplate}>
                                Download Template
                            </Button>
                            <Button variant="outlined" component="label">
                                Upload CSV
                                <input
                                    type="file"
                                    accept=".csv,text/csv"
                                    hidden
                                    onChange={handleFileUpload}
                                />
                            </Button>
                        </Stack>
                        <Typography variant="caption" color="textSecondary">
                            Columns: {CSV_COLUMNS.join(", ")}
                        </Typography>
                        <TextField
                            label="Or paste CSV here"
                            multiline
                            minRows={4}
                            fullWidth
                            value={importText}
                            onChange={(e) => setImportText(e.target.value)}
                            variant="outlined"
                        />
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => runParse(importText)}
                            disabled={isBlank(importText)}
                            sx={{ alignSelf: "flex-start" }}
                        >
                            Parse &amp; Validate
                        </Button>
                        {hasParsed && (
                            <>
                                <Divider />
                                <Typography variant="subtitle1">
                                    {parsedRecords.length} row(s) parsed
                                    {importErrorCount > 0
                                        ? ` — ${importErrorCount} with errors`
                                        : " — all valid"}
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <tr>
                                            {CSV_COLUMNS.map((c) => (
                                                <th
                                                    key={c}
                                                    style={{ textAlign: "left", padding: "4px 8px" }}
                                                >
                                                    {c}
                                                </th>
                                            ))}
                                            <th style={{ textAlign: "left", padding: "4px 8px" }}>
                                                Issues
                                            </th>
                                        </tr>
                                    </TableHead>
                                    <tbody>
                                        {parsedRecords.map((r, idx) => (
                                            <tr key={idx}>
                                                {CSV_COLUMNS.map((c) => (
                                                    <td key={c} style={{ padding: "4px 8px" }}>
                                                        {String(r.record[c] ?? "")}
                                                    </td>
                                                ))}
                                                <td style={{ padding: "4px 8px", color: "#d32f2f" }}>
                                                    {r.errors.join("; ")}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleImportClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleLoadImport}
                        disabled={!canLoadImport}
                    >
                        Load into grid
                    </Button>
                </DialogActions>
            </Dialog>

            <RiderRatesTestDialog
                open={testDialogOpen}
                onClose={handleTestClose}
                title="Test Vendor Rider Rates Output"
                isLoading={testDialogLoading}
                loadingMessage={testLoadingMessage}
                bannerMessage={testBannerMessage}
                testKwhInput={testKwhInput}
                onTestKwhChange={handleTestKwhChange}
                billingDate={testBillingDate}
                onBillingDateChange={handleTestBillingDateChange}
                onRefreshTest={calculateTestRiderRate}
                summary={riderRateTestProductSummary}
                riders={riderRateTestProduct}
                baseSummary={testBaseSummary}
                showCommonAreaTotal={false}
            />
        </Card>
    );
}

VendorRiderRatesTable.propTypes = {
    vendor: PropTypes.object.isRequired,
    effectiveAsOfDate: PropTypes.string,
    user: PropTypes.object.isRequired,
    vendors: PropTypes.object.isRequired,
    updateLocalVendorRiderRates: PropTypes.func.isRequired,
    getVendorRiderRatesByVendorUuid: PropTypes.func.isRequired,
    createVendorRiderRate: PropTypes.func.isRequired,
    updateVendorRiderRate: PropTypes.func.isRequired,
    deleteVendorRiderRate: PropTypes.func.isRequired,
};

function State(state) {
    return {
        user: state.user,
        vendors: state.vendors,
    };
}

export default connect(State, {
    updateLocalVendorRiderRates,
    getVendorRiderRatesByVendorUuid,
    createVendorRiderRate,
    updateVendorRiderRate,
    deleteVendorRiderRate,
})(VendorRiderRatesTable);
